import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Sparkles, Loader2, ChevronLeft, ChevronRight, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import SpendingChart from '../components/insights/SpendingChart';
import CategoryBreakdown from '../components/insights/CategoryBreakdown';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6', '#6366f1', '#84cc16', '#06b6d4', '#f97316', '#a855f7'];
const fmt = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 }).format(n || 0);

export default function Insights() {
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  const { data: receipts = [] } = useQuery({ queryKey: ['receipts'], queryFn: async () => { const { data } = await api.get('/api/receipts?sort=-date&limit=500'); return data; } });

  const monthReceipts = receipts.filter(r => { const d = new Date(r.date); return d >= startOfMonth(selectedMonth) && d <= endOfMonth(selectedMonth); });
  const lastMonth = subMonths(selectedMonth, 1);
  const lastMonthReceipts = receipts.filter(r => { const d = new Date(r.date); return d >= startOfMonth(lastMonth) && d <= endOfMonth(lastMonth); });

  const thisTotal = monthReceipts.reduce((s, r) => s + (r.amount || 0), 0);
  const lastTotal = lastMonthReceipts.reduce((s, r) => s + (r.amount || 0), 0);
  const change = lastTotal > 0 ? ((thisTotal - lastTotal) / lastTotal * 100) : 0;

  // Category data
  const categoryData = useMemo(() => {
    const cats = monthReceipts.reduce((acc, r) => { const c = r.category || 'other'; acc[c] = (acc[c] || 0) + (r.amount || 0); return acc; }, {});
    return Object.entries(cats).map(([name, value]) => ({ name: name.replace(/_/g, ' '), value })).sort((a, b) => b.value - a.value);
  }, [monthReceipts]);

  // Day-of-week data
  const dayData = useMemo(() => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const totals = new Array(7).fill(0);
    monthReceipts.forEach(r => { const day = new Date(r.date).getDay(); totals[day === 0 ? 6 : day - 1] += r.amount || 0; });
    return days.map((d, i) => ({ day: d, amount: totals[i] }));
  }, [monthReceipts]);

  const paymentData = useMemo(() => {
    const methods = monthReceipts.reduce((acc, r) => { const m = r.payment_method || 'other'; acc[m] = (acc[m] || 0) + (r.amount || 0); return acc; }, {});
    return Object.entries(methods).map(([name, value]) => ({ name: name.replace(/_/g, ' '), value })).sort((a, b) => b.value - a.value);
  }, [monthReceipts]);

  const fetchAiInsights = async () => {
    setLoadingInsights(true);
    try {
      const { data } = await api.post('/api/ai/insights', {
        spending_data: { total: thisTotal, lastMonthTotal: lastTotal, change: change.toFixed(1),
          topCategories: categoryData.slice(0, 5), receiptCount: monthReceipts.length,
          taxDeductible: monthReceipts.filter(r => r.tax_deductible).reduce((s, r) => s + r.amount * (r.tax_split_percentage || 100) / 100, 0),
          gstCredits: monthReceipts.filter(r => r.gst_type === 'gst_10').reduce((s, r) => s + (r.gst_amount || r.amount / 11), 0) }
      });
      setAiInsights(data);
    } catch (e) { console.error('AI insights error:', e); }
    setLoadingInsights(false);
  };

  const navigateMonth = (dir) => { const d = new Date(selectedMonth); d.setMonth(d.getMonth() + dir); setSelectedMonth(d); setAiInsights(null); };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="insights-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div><h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Insights</h1><p className="text-slate-500 mt-1">Understand your spending patterns</p></div>
          <Button onClick={fetchAiInsights} disabled={loadingInsights || receipts.length === 0} className="bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600" data-testid="get-ai-insights-button">
            {loadingInsights ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyzing...</> : <><Sparkles className="w-4 h-4 mr-2" />AI Insights</>}
          </Button>
        </div>
        <div className="flex items-center justify-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigateMonth(-1)}><ChevronLeft className="w-5 h-5" /></Button>
          <h2 className="text-xl font-semibold text-slate-900 min-w-[180px] text-center">{format(selectedMonth, 'MMMM yyyy')}</h2>
          <Button variant="ghost" size="icon" onClick={() => navigateMonth(1)}><ChevronRight className="w-5 h-5" /></Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-5 border border-slate-100"><p className="text-sm text-slate-500">This Month</p><p className="text-3xl font-bold text-slate-900 mt-1">{fmt(thisTotal)}</p><p className={`text-sm mt-2 ${change > 0 ? 'text-red-500' : 'text-emerald-500'}`}>{change > 0 ? '+' : ''}{change.toFixed(1)}% vs last month</p></div>
          <div className="bg-white rounded-2xl p-5 border border-slate-100"><p className="text-sm text-slate-500">Transactions</p><p className="text-3xl font-bold text-slate-900 mt-1">{monthReceipts.length}</p><p className="text-sm text-slate-400 mt-2">this month</p></div>
          <div className="bg-white rounded-2xl p-5 border border-slate-100"><p className="text-sm text-slate-500">Avg per Receipt</p><p className="text-3xl font-bold text-slate-900 mt-1">{fmt(monthReceipts.length > 0 ? thisTotal / monthReceipts.length : 0)}</p></div>
        </div>

        {/* AI Insights */}
        <AnimatePresence>
          {aiInsights && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl border border-violet-200 p-6 mb-8">
              <div className="flex items-center gap-2 mb-4"><Sparkles className="w-5 h-5 text-violet-600" /><h3 className="font-semibold text-violet-900">AI Insights</h3></div>
              {aiInsights.summary && <p className="text-sm text-violet-700 mb-4">{aiInsights.summary}</p>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {aiInsights.insights?.map((insight, i) => (
                  <div key={i} className="bg-white/80 backdrop-blur-sm rounded-xl p-4"><h4 className="font-medium text-violet-900">{insight.title}</h4><p className="text-sm text-violet-700 mt-1">{insight.description}</p>{insight.potential_savings > 0 && <p className="text-xs text-violet-500 mt-2 font-medium">Potential savings: {fmt(insight.potential_savings)}</p>}</div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 border border-slate-100"><h3 className="font-semibold text-slate-900 mb-4">Spending Trend (6 months)</h3><SpendingChart receipts={receipts} months={6} /></div>
          <div className="bg-white rounded-2xl p-6 border border-slate-100"><h3 className="font-semibold text-slate-900 mb-4">Category Breakdown</h3><CategoryBreakdown receipts={monthReceipts} /></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Day of Week */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100">
            <h3 className="font-semibold text-slate-900 mb-4">Spending by Day</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={dayData} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Bar dataKey="amount" fill="#10b981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {/* Payment Methods */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100">
            <h3 className="font-semibold text-slate-900 mb-4">Payment Methods</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={paymentData} layout="vertical" barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} width={100} className="capitalize" />
                <Tooltip formatter={(v) => fmt(v)} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]}>{paymentData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
